import axios from 'axios'
import api, { refreshAccessToken } from './apiRequester'
import { normalisePageOverrides } from '../access/pageAccess'

/**
 * Local development session bootstrap.
 *
 * Lets `npm start` open the dashboard in a fresh browser profile (a second
 * browser, an automated one used for visual checks, etc.) without clicking
 * through the Cognito hosted UI every time. It is NOT an auth bypass:
 *
 *   - It uses a real Cognito refresh token for a real user, minted through
 *     the same token endpoint the axios interceptor already uses, so the API
 *     sees a genuine access token and the user's genuine org/admin rights.
 *   - Nothing changes server-side. `authDashboard` still validates the JWT
 *     signature and asks Cognito for the user profile on every request.
 *
 * Guardrails:
 *
 *   - Only active when `NODE_ENV === 'development'`. CRA replaces that at
 *     build time, so in `npm run build` the whole branch is dead code and
 *     terser drops it.
 *   - The token comes from `REACT_APP_DEV_REFRESH_TOKEN`, which must live in
 *     `.env.development.local` (gitignored; CRA only loads `*.development.*`
 *     files for `npm start`, never for production builds).
 *   - Only runs when the page is served from a loopback host.
 *   - The token is never read from the URL, never logged, and is only written
 *     to `localStorage.refresh` — exactly where a normal login stores it.
 *
 * To (re)arm it: in a logged-in browser, DevTools → Application → Local
 * Storage → copy the `refresh` value into `.env.development.local` as
 * `REACT_APP_DEV_REFRESH_TOKEN=...`, then restart `npm start`. Cognito refresh
 * tokens live ~30 days; when this one expires the bootstrap fails quietly and
 * the normal Cognito login takes over.
 */

const IS_DEV = process.env.NODE_ENV === 'development'
const DEV_REFRESH_TOKEN = IS_DEV ? (process.env.REACT_APP_DEV_REFRESH_TOKEN || '').trim() : ''

const isLoopbackHost = () => ['localhost', '127.0.0.1', '[::1]'].includes(window.location.hostname)

export const isDevSessionEnabled = (): boolean => IS_DEV && DEV_REFRESH_TOKEN.length > 0 && isLoopbackHost()

/** True when the same keys auth.tsx writes after a hosted-UI login are present. */
export const hasStoredSession = (): boolean =>
    Boolean(window.localStorage.getItem('access') && window.localStorage.getItem('user'))

/** Primary org from the stored session, if any. */
export const storedPrimaryOrganisation = (): string => {
    try {
        const orgs = JSON.parse(window.localStorage.getItem('userOrganisations') || 'null')
        if (orgs?.primary) return String(orgs.primary)
        const user = JSON.parse(window.localStorage.getItem('user') || 'null')
        return user?.['custom:organisation'] || ''
    } catch {
        return ''
    }
}

/**
 * Mint a real access token from the dev refresh token and populate the same
 * localStorage keys `containers/auth.tsx` writes after the code exchange, so
 * `Main` rehydrates the auth context exactly as it would after a hard refresh.
 * Resolves true on success; false when disabled, already signed in, or failed.
 */
export const bootstrapDevSession = async (): Promise<boolean> => {
    if (!isDevSessionEnabled()) return false
    if (hasStoredSession()) return true

    try {
        window.localStorage.setItem('refresh', DEV_REFRESH_TOKEN)
        const accessToken = await refreshAccessToken()

        const userResponse = await axios.get(`${process.env.REACT_APP_OAUTH_USER}`, {
            headers: { Authorization: `Bearer ${accessToken}` },
        })
        const user = userResponse.data
        window.localStorage.setItem('user', JSON.stringify(user))

        const initData = (await api({ url: '/init' }))?.data || {}
        const primary: string = initData.primary || user['custom:organisation'] || ''
        const organisations: string[] =
            Array.isArray(initData.organisations) && initData.organisations.length > 0
                ? initData.organisations
                : [primary].filter(Boolean)
        const pageOverridesByOrg = normalisePageOverrides(initData.pageOverridesByOrg)
        window.localStorage.setItem('userOrganisations', JSON.stringify({ primary, organisations, pageOverridesByOrg }))

        const tl = initData.teamLeader || {}
        window.localStorage.setItem(
            'pcpReferralTeamLeader',
            JSON.stringify({
                isLeader: Boolean(tl.isLeader),
                teamId: tl.team_id ?? tl.teamId ?? null,
                teamName: tl.team_name ?? tl.teamName ?? null,
            }),
        )

        console.warn(
            `[dev-session] Signed in as ${user.email || 'unknown user'} (${primary}) from REACT_APP_DEV_REFRESH_TOKEN. ` +
                'Development builds on localhost only.',
        )
        return true
    } catch (error: any) {
        // Expired/revoked token or Cognito unreachable: drop what we seeded so
        // the interceptor does not keep retrying with it, and let the normal
        // hosted-UI login handle things.
        for (const key of ['access', 'id', 'refresh', 'user', 'userOrganisations', 'pcpReferralTeamLeader']) {
            window.localStorage.removeItem(key)
        }
        console.warn(
            '[dev-session] Could not start a session from REACT_APP_DEV_REFRESH_TOKEN — falling back to Cognito login.',
            error?.response?.data?.error || error?.message || error,
        )
        return false
    }
}
